import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Text } from '@cottonray/ui-web'
import type {
  AccountCapabilities,
  AccountFailure,
  AccountLinkedProvider,
  AccountProfile,
} from '@cottonray/auth-contract'
import { accountBridge, inApp, type Result } from './account/bridge'
import { accountLabels, type AccountLabels } from './account/labels'
import type { AccountContext } from './account/params'
import { accentOverride } from './support/params'

/**
 * 공용 "내 계정" 화면 — **전 앱이 이 한 페이지를 연다** (2026-08-26 사용자 지시:
 * "프로필 페이지도 각 앱에서 지원하기 페이지처럼 웹뷰로 구현해주고. (세션 등 완벽히 유지)
 * (공통페이지 1개로 모두 관리) (테마 유지)").
 *
 * 앱마다 있던 네이티브 화면(`@cottonray/auth-native` 의 `AccountScreen` 626줄)을 대신한다.
 * 문의 화면이 먼저 간 길과 같고, 이유도 같다: **문구 한 줄·칸 하나를 고치려고 앱을 다시
 * 심사에 올리는 구조**를 없애기 위해서다.
 *
 * ## 세션은 이 페이지에 없다
 *
 * 토큰을 받지 않는다. 프로필을 읽는 것도, 비밀번호를 바꾸는 것도 **앱에게 부탁**하고
 * 결과만 그린다(`account/bridge.ts`). 그래서 "세션을 완벽히 유지한다"가 약속이 아니라
 * 구조다 — 이 페이지에는 유출할 세션 자체가 없다.
 *
 * ## 화면 구성은 앱 시절 그대로다
 *
 * 개요 → (프로필 수정 · 비밀번호 변경 · 연결된 계정). 하위 화면을 라우트로 쪼개지 않고
 * 이 컴포넌트가 들고 있는 것도 그때와 같다 — 앱의 뒤로가기가 웹뷰 히스토리를 되짚으므로
 * 주소를 바꾸면 앱 뒤로가기가 이 화면 안을 오가게 되어 오히려 헷갈린다.
 */
export interface AccountPageProps {
  context: AccountContext
}

/** 서버(`ChangePasswordRequest`)의 `@Size(min = 8)` 과 같은 값이어야 한다 */
const MIN_PASSWORD_LENGTH = 8

/** 모양만 본다 — 정규식으로 RFC 를 흉내 내면 멀쩡한 주소를 막는 쪽으로 틀린다 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type View = 'overview' | 'edit' | 'password' | 'linked'

export function AccountPage({ context }: AccountPageProps) {
  const labels = accountLabels(context.language)
  const [view, setView] = useState<View>('overview')
  const [profile, setProfile] = useState<AccountProfile | null>(null)
  const [can, setCan] = useState<AccountCapabilities | null>(null)
  const [avatar, setAvatar] = useState<string | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')

  const load = useCallback(async () => {
    setState('loading')
    // 프로필과 가능 목록을 함께 기다린다 — 따로 그리면 메뉴가 한 박자 늦게 나타나
    // 사용자가 이미 스크롤을 시작한 뒤에 항목이 끼어든다 (앱 시절과 같은 판단)
    const [who, capabilities, linked] = await Promise.all([
      accountBridge.profile(),
      accountBridge.capabilities(),
      accountBridge.linkedAccounts(),
    ])
    if (!who.ok || !capabilities.ok) {
      setState('failed')
      return
    }
    // Native provider grants do not carry the browser broker's identity_provider claim.
    // Use the confirmed account links before labeling a social account as password login.
    const connectedProvider = linked.ok
      ? linked.value.find(row => row.connected && ['google', 'apple', 'kakao'].includes(row.providerAlias))?.providerAlias
      : undefined
    setProfile({ ...who.value, socialProvider: who.value.socialProvider ?? (
      connectedProvider === 'google' || connectedProvider === 'apple' || connectedProvider === 'kakao'
        ? connectedProvider : null
    ) })
    setCan(capabilities.value)
    setState('ready')
    // 사진은 늦게 와도 화면을 막지 않는다 — 몇백 KB 를 다리로 실어 오기 때문이다
    void accountBridge.avatar().then((result) => {
      if (result.ok) setAvatar(result.value)
    })
  }, [])

  useEffect(() => {
    if (inApp()) void load()
  }, [load])

  // 앱이 여는 입구지 검색에서 찾아올 페이지가 아니다 (문의 화면과 같다)
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
    document.documentElement.lang = context.language
    return () => meta.remove()
  }, [context.language])

  const shell = `account${context.embed ? ' account--embed' : ''}`

  // 앱 밖에서 열면 할 수 있는 것이 없다. 그건 오류가 아니라 안내할 상황이다
  if (!inApp()) {
    return (
      <main className={shell}>
        {context.accentHex !== undefined && <style>{accentOverride(context.accentHex)}</style>}
        <Card>
          <Text variant="title">{labels.appOnlyTitle}</Text>
          <Text tone="muted" className="account__lead">
            {labels.appOnlyBody}
          </Text>
        </Card>
      </main>
    )
  }

  return (
    <main className={shell}>
      {context.accentHex !== undefined && <style>{accentOverride(context.accentHex)}</style>}
      {!context.embed && (
        <header className="account__head">
          <Text variant="title" as="h1">
            {labels.title}
          </Text>
        </header>
      )}

      {state === 'loading' && (
        <Card>
          <Text tone="muted">{labels.loading}…</Text>
        </Card>
      )}

      {state === 'failed' && (
        <Card>
          <Text variant="title">{labels.loadFailedTitle}</Text>
          <Text tone="muted" className="account__lead">
            {labels.loadFailedBody}
          </Text>
          <div className="account__actions">
            <Button variant="secondary" onClick={() => void load()}>
              {labels.retry}
            </Button>
          </div>
        </Card>
      )}

      {state === 'ready' && profile !== null && can !== null && (
        <>
          {view === 'overview' && (
            <Overview
              labels={labels}
              profile={profile}
              can={can}
              avatar={avatar}
              onEdit={() => setView('edit')}
              onPassword={() => setView('password')}
              onLinked={() => setView('linked')}
            />
          )}
          {view === 'edit' && (
            <EditProfile
              labels={labels}
              profile={profile}
              can={can}
              avatar={avatar}
              onAvatar={setAvatar}
              onBack={() => setView('overview')}
              onSaved={() => {
                setView('overview')
                void load()
              }}
            />
          )}
          {view === 'password' && (
            <ChangePassword labels={labels} onBack={() => setView('overview')} />
          )}
          {view === 'linked' && (
            <Linked labels={labels} profile={profile} onBack={() => setView('overview')} />
          )}
        </>
      )}
    </main>
  )
}

/** 실패 사유를 이 화면의 언어로 옮긴다 — 앱이 만든 한국어 문장을 그대로 띄우지 않는다 */
function messageFor(labels: AccountLabels, reason: AccountFailure): string {
  switch (reason) {
    case 'network':
      return labels.errorNetwork
    case 'denied':
      return labels.errorDenied
    case 'conflict':
      return labels.errorConflict
    case 'invalid':
      return labels.errorInvalid
    case 'unsupported':
      return labels.errorUnsupported
  }
}

function Avatar({ src, name }: { src: string | null; name: string }) {
  if (src !== null) return <img className="account-avatar" src={src} alt="" />
  // 사진이 없으면 이름 첫 글자 — 빈 동그라미보다 자기 계정이라는 것이 분명하다
  return <div className="account-avatar account-avatar--initial">{[...name][0] ?? '?'}</div>
}

function Overview({
  labels,
  profile,
  can,
  avatar,
  onEdit,
  onPassword,
  onLinked,
}: {
  labels: AccountLabels
  profile: AccountProfile
  can: AccountCapabilities
  avatar: string | null
  onEdit: () => void
  onPassword: () => void
  onLinked: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  async function remove(): Promise<void> {
    setBusy(true)
    setError(undefined)
    const result = await accountBridge.deleteAccount()
    // 성공하면 앱이 화면을 걷어 간다 — 여기서 더 그릴 것이 없다
    if (!result.ok) {
      setBusy(false)
      setError(messageFor(labels, result.reason))
    }
  }

  return (
    <>
      {/* 아이디를 여기 두지 않는다 (2026-08-07 사용자 지시, 앱 시절 그대로) — 카카오
          브로커 계정의 username 은 숫자 id 라 사람이 알아볼 수 없고, 첫 화면에 뜨면
          자기 계정이 맞나 의심하게 만든다 */}
      <Card>
        <div className="account-identity">
          <Avatar src={avatar} name={profile.displayName} />
          <div className="account-identity__text">
            <Text variant="title">{profile.displayName}</Text>
            {profile.email !== null && (
              <Text variant="caption" tone="muted">
                {profile.email}
              </Text>
            )}
          </div>
          <Button variant="ghost" onClick={onEdit} aria-label={labels.editProfile}>
            {labels.editProfile}
          </Button>
        </div>
      </Card>

      <div className="account-rows">
        {/* 소셜로 들어온 계정에는 우리 쪽 비밀번호가 없다 — 보여 주면 눌러도 바꿀 것이
            없어 사용자를 헤매게 한다 */}
        {profile.socialProvider === null && can.canChangePassword && (
          <button type="button" className="account-row" onClick={onPassword}>
            <span className="account-row__text">
              <Text variant="label">{labels.changePassword}</Text>
              <Text variant="caption" tone="muted">
                {labels.changePasswordHint}
              </Text>
            </span>
            <span aria-hidden className="account-row__chevron">
              ›
            </span>
          </button>
        )}
        <button type="button" className="account-row" onClick={onLinked}>
          <span className="account-row__text">
            <Text variant="label">{labels.linkedAccounts}</Text>
            <Text variant="caption" tone="muted">
              {providerName(labels, profile.socialProvider)}
            </Text>
          </span>
          <span aria-hidden className="account-row__chevron">
            ›
          </span>
        </button>
      </div>

      <div className="account__actions account__actions--stack">
        <Button variant="secondary" onClick={() => void accountBridge.logout()}>
          {labels.logout}
        </Button>

        {can.canDeleteAccount &&
          (confirming ? (
            <Card>
              <Text variant="label">{labels.deleteTitle}</Text>
              <Text tone="muted" className="account__lead">
                {can.deletionNote}
              </Text>
              {error !== undefined && (
                <Text tone="danger" role="alert" className="account__lead">
                  {error}
                </Text>
              )}
              <div className="account__actions">
                <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                  {labels.deleteCancel}
                </Button>
                <Button variant="secondary" className="account-danger" onClick={() => void remove()} disabled={busy}>
                  {busy ? labels.deleting : labels.deleteConfirm}
                </Button>
              </div>
            </Card>
          ) : (
            <Button variant="ghost" className="account-danger" onClick={() => setConfirming(true)}>
              {labels.deleteAccount}
            </Button>
          ))}
      </div>
    </>
  )
}

function EditProfile({
  labels,
  profile,
  can,
  avatar,
  onAvatar,
  onBack,
  onSaved,
}: {
  labels: AccountLabels
  profile: AccountProfile
  can: AccountCapabilities
  avatar: string | null
  onAvatar: (next: string | null) => void
  onBack: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(profile.displayName)
  const [email, setEmail] = useState(profile.email ?? '')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  async function save(): Promise<void> {
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()

    if (trimmedName === '') {
      setError(labels.nameRequired)
      return
    }
    if (trimmedEmail !== '' && !EMAIL_SHAPE.test(trimmedEmail)) {
      setError(labels.emailInvalid)
      return
    }

    setBusy(true)
    setError(undefined)
    const result = await accountBridge.updateProfile(trimmedName, trimmedEmail)
    setBusy(false)
    if (result.ok) onSaved()
    else setError(messageFor(labels, result.reason))
  }

  /**
   * 사진 고르기 → 올리기. **고르자마자 올린다** (앱 시절과 같다) — "저장"을 눌러야
   * 반영되게 하면 사진과 이름의 저장 시점이 갈려 어느 쪽이 저장됐는지 알기 어려워진다.
   */
  async function choose(source: 'camera' | 'library'): Promise<void> {
    setBusy(true)
    setError(undefined)
    const result = await accountBridge.pickPhoto(source)
    setBusy(false)
    // 취소하면 null 이 온다 — 조용히 돌아간다
    if (!result.ok) setError(messageFor(labels, result.reason))
    else if (result.value !== null) onAvatar(result.value)
  }

  async function removePhoto(): Promise<void> {
    setBusy(true)
    setError(undefined)
    const result: Result<null> = await accountBridge.deletePhoto()
    setBusy(false)
    if (result.ok) onAvatar(null)
    else setError(messageFor(labels, result.reason))
  }

  const canPhoto = can.canUploadPhoto && can.canPickPhoto

  return (
    <>
      <SubHeader labels={labels} title={labels.editTitle} onBack={onBack} />

      {canPhoto && (
        <Card>
          <div className="account-photo">
            <Avatar src={avatar} name={profile.displayName} />
            <div className="account-photo__actions">
              <Button variant="secondary" onClick={() => void choose('camera')} disabled={busy}>
                {labels.photoFromCamera}
              </Button>
              <Button variant="secondary" onClick={() => void choose('library')} disabled={busy}>
                {labels.photoFromLibrary}
              </Button>
              {/* 없는 사진을 지우는 버튼을 보여 주지 않는다 */}
              {avatar !== null && (
                <Button variant="ghost" className="account-danger" onClick={() => void removePhoto()} disabled={busy}>
                  {labels.photoRemove}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <form
        className="account-form"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <label className="account-field">
          <Text variant="label">{labels.nameLabel}</Text>
          <input
            className="account-field__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={labels.namePlaceholder}
            maxLength={30}
            disabled={busy}
          />
          <Text variant="caption" tone="muted">
            {labels.nameHelper}
          </Text>
        </label>

        <label className="account-field">
          <Text variant="label">{labels.emailLabel}</Text>
          <input
            className="account-field__input"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={labels.emailPlaceholder}
            maxLength={254}
            disabled={busy}
          />
        </label>

        {error !== undefined && (
          <Text tone="danger" role="alert">
            {error}
          </Text>
        )}

        <Button type="submit" disabled={busy}>
          {labels.save}
        </Button>
      </form>
    </>
  )
}

/**
 * 비밀번호 변경. **현재 비밀번호를 반드시 받는다** — 기기를 잠깐 빌린 사람이 비밀번호를
 * 갈아 치우고 계정을 가져가는 것을 막는 건 이 칸 하나다. 확인은 서버가 한다.
 */
function ChangePassword({ labels, onBack }: { labels: AccountLabels; onBack: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function save(): Promise<void> {
    if (current === '') return setError(labels.currentRequired)
    if (next.length < MIN_PASSWORD_LENGTH) return setError(labels.tooShort(MIN_PASSWORD_LENGTH))
    if (next !== confirm) return setError(labels.mismatch)
    if (next === current) return setError(labels.samePassword)

    setBusy(true)
    setError(undefined)
    const result = await accountBridge.changePassword(current, next)
    setBusy(false)
    if (result.ok) setDone(true)
    else setError(messageFor(labels, result.reason))
  }

  if (done) {
    return (
      <>
        <SubHeader labels={labels} title={labels.passwordTitle} onBack={onBack} />
        <Card>
          <Text variant="title">{labels.passwordChanged}</Text>
          <Text tone="muted" className="account__lead">
            {labels.passwordChangedBody}
          </Text>
          <div className="account__actions">
            <Button onClick={onBack}>{labels.back}</Button>
          </div>
        </Card>
      </>
    )
  }

  return (
    <>
      <SubHeader labels={labels} title={labels.passwordTitle} onBack={onBack} />
      <form
        className="account-form"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <PasswordField
          label={labels.currentPassword}
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
          disabled={busy}
        />
        <PasswordField
          label={labels.newPassword}
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          helper={labels.newPasswordHelper(MIN_PASSWORD_LENGTH)}
          disabled={busy}
        />
        <PasswordField
          label={labels.confirmPassword}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          disabled={busy}
        />

        {error !== undefined && (
          <Text tone="danger" role="alert">
            {error}
          </Text>
        )}

        <Button type="submit" disabled={busy}>
          {labels.change}
        </Button>
      </form>
    </>
  )
}

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  helper,
  disabled,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  autoComplete: string
  helper?: string
  disabled: boolean
}) {
  return (
    <label className="account-field">
      <Text variant="label">{label}</Text>
      <input
        className="account-field__input"
        type="password"
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      {helper !== undefined && (
        <Text variant="caption" tone="muted">
          {helper}
        </Text>
      )}
    </label>
  )
}

/**
 * 연결된 계정. **보기 전용이다** (앱 시절 그대로) — 연결 해제를 넣으려면 마지막 로그인
 * 수단을 끊어 스스로 잠기는 경우를 막아야 하는데, 그 판단(비밀번호가 있는가·다른 소셜이
 * 남는가)을 클라이언트에서 하면 경쟁 조건이 생긴다. 필요해지면 서버가 판단해 주는 형태로 올린다.
 */
function Linked({
  labels,
  profile,
  onBack,
}: {
  labels: AccountLabels
  profile: AccountProfile
  onBack: () => void
}) {
  const [rows, setRows] = useState<AccountLinkedProvider[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    void accountBridge.linkedAccounts().then((result) => {
      if (result.ok) setRows(result.value)
      else setFailed(true)
    })
  }, [])

  return (
    <>
      <SubHeader labels={labels} title={labels.linkedTitle} onBack={onBack} />
      <Card>
        {/* 목록을 못 읽어도 빈 화면을 주지 않는다 — 토큰이 아는 것 하나라도 보여 준다 */}
        {rows === null || rows.length === 0 ? (
          <div className="account-linked__row">
            <Text variant="label">{providerName(labels, profile.socialProvider)}</Text>
            <Text variant="caption" tone="muted">
              {failed || rows === null ? '' : labels.connected}
            </Text>
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.providerAlias} className="account-linked__row">
              <div>
                <Text variant="label">{row.displayName}</Text>
                {row.linkedUsername !== null && (
                  <Text variant="caption" tone="muted">
                    {row.linkedUsername}
                  </Text>
                )}
              </div>
              <Text variant="caption" tone={row.connected ? 'muted' : 'subtle'}>
                {row.connected ? labels.connected : labels.notConnected}
              </Text>
            </div>
          ))
        )}
      </Card>
      <Text variant="caption" tone="subtle" className="account__lead">
        {labels.linkedReadOnly}
      </Text>
    </>
  )
}

function SubHeader({
  labels,
  title,
  onBack,
}: {
  labels: AccountLabels
  title: string
  onBack: () => void
}) {
  return (
    <div className="account-subhead">
      <Button variant="ghost" onClick={onBack}>
        ‹ {labels.back}
      </Button>
      <Text variant="label">{title}</Text>
    </div>
  )
}

function providerName(labels: AccountLabels, provider: AccountProfile['socialProvider']): string {
  switch (provider) {
    case 'kakao':
      return '카카오'
    case 'google':
      return 'Google'
    case 'apple':
      return 'Apple'
    default:
      return labels.passwordLogin
  }
}
